import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { FindSchemasBySleepScreenId } from '../../libs/GraphQL';
import { isActiveSchema } from '../../utils/schemaLifecycle';
import { getActiveSchemaIdSet } from '../../utils/activeSchemaIds';
import styles from './AssetAssignmentSection.module.css';

const SLOT_LABELS = {
  standard: 'Standard',
  newFridgeExperience: 'New Fridge Experience',
  happyHourAm: 'AM Happy Hour',
  happyHourPm: 'PM Happy Hour',
};

const PAGE_SIZE = 100;

// Path separator chars + nul char get stripped so the filename is filesystem-safe
// across browsers without relying on each browser's own sanitization.
const sanitizeFilenamePart = (value) =>
  String(value || '').replace(/[/\\?%*:|"<> ]/g, '_');

const AssetAssignmentSection = ({ sleepScreenId, forceRefresh }) => {
  const [assignments, setAssignments] = useState([]);
  const [status, setStatus] = useState('loading');
  const [searchTerm, setSearchTerm] = useState('');
  const [visibleLimit, setVisibleLimit] = useState(PAGE_SIZE);

  useEffect(() => {
    let cancelled = false;
    const fetchAssignments = async () => {
      setStatus('loading');
      try {
        // null on failure so a fleet hiccup degrades to the unfiltered list
        // rather than erroring the section; the assignments fetch still throws.
        const [result, activeSchemaIds] = await Promise.all([
          FindSchemasBySleepScreenId(sleepScreenId, {
            force: Boolean(forceRefresh),
          }),
          getActiveSchemaIdSet().catch(() => null),
        ]);
        if (cancelled) return;
        // Count active fridges (live, or launching and not yet open); drop
        // decommissioned fridges and base templates so this count matches the
        // library + assignment tool.
        const rows = result || [];
        setAssignments(
          activeSchemaIds
            ? rows.filter((row) =>
                isActiveSchema(row.schemaId, activeSchemaIds, {
                  includeBaseSchemas: false,
                }),
              )
            : rows,
        );
        setStatus('loaded');
      } catch (err) {
        if (cancelled) return;
        console.error('findSchemasBySleepScreenId failed:', err);
        toast.error('Failed to load fridge assignments.');
        setStatus('error');
      }
    };
    fetchAssignments();
    return () => {
      cancelled = true;
    };
  }, [sleepScreenId, forceRefresh]);

  const filteredAssignments = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return assignments;
    return assignments.filter((a) =>
      (a.schemaId || '').toLowerCase().includes(term),
    );
  }, [assignments, searchTerm]);

  useEffect(() => {
    setVisibleLimit(PAGE_SIZE);
  }, [searchTerm]);

  const visibleAssignments = filteredAssignments.slice(0, visibleLimit);
  const hasMore = filteredAssignments.length > visibleLimit;

  const handleCopyFridgeList = async () => {
    if (filteredAssignments.length === 0) return;
    const text = filteredAssignments.map((a) => a.schemaId).join('\n');
    try {
      await navigator.clipboard.writeText(text);
      toast.success(
        `Copied ${filteredAssignments.length} fridge ids to clipboard.`,
      );
    } catch (err) {
      console.error('Clipboard write failed:', err);
      toast.error('Failed to copy to clipboard.');
    }
  };

  const handleDownloadCsv = () => {
    if (filteredAssignments.length === 0) return;
    const escape = (value) => {
      const s = value === null || value === undefined ? '' : String(value);
      if (/[,"\n\r]/.test(s)) {
        return `"${s.replace(/"/g, '""')}"`;
      }
      return s;
    };
    const lines = [
      'schemaId,slot',
      ...filteredAssignments.map(
        (a) => `${escape(a.schemaId)},${escape(a.slot)}`,
      ),
    ];
    const blob = new Blob([lines.join('\r\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const today = new Date().toISOString().slice(0, 10);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${sanitizeFilenamePart(
      sleepScreenId,
    )}_assignments_${today}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    // Defer revoke so the browser's async download handler has read the blob.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  if (status === 'loading') {
    return (
      <section className={styles.section}>
        <h2 className={styles.heading}>Asset Assignment</h2>
        <div className={styles.stateBlock}>Loading…</div>
      </section>
    );
  }

  if (status === 'error') {
    return (
      <section className={styles.section}>
        <h2 className={styles.heading}>Asset Assignment</h2>
        <div className={styles.stateBlock}>
          Couldn’t load fridge assignments for this entry.
        </div>
      </section>
    );
  }

  const totalCount = assignments.length;
  const filteredCount = filteredAssignments.length;
  const hasSearch = searchTerm.trim().length > 0;

  const renderBody = () => {
    if (totalCount === 0) {
      return (
        <div className={styles.stateBlock}>
          No fridges are currently assigned to this entry.
        </div>
      );
    }
    if (filteredCount === 0) {
      return (
        <div className={styles.stateBlock}>
          No fridges match &quot;{searchTerm.trim()}&quot;.
        </div>
      );
    }
    return (
      <>
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Fridge ID</th>
                <th>Slot</th>
              </tr>
            </thead>
            <tbody>
              {visibleAssignments.map((row) => (
                <tr key={JSON.stringify([row.schemaId, row.slot])}>
                  <td className={styles.mono}>{row.schemaId}</td>
                  <td>{SLOT_LABELS[row.slot] || row.slot}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {hasMore && (
          <div className={styles.loadMoreRow}>
            <span className={styles.loadMoreCount}>
              Showing {visibleAssignments.length} of {filteredCount}
            </span>
            <button
              type="button"
              className={styles.loadMoreButton}
              onClick={() => setVisibleLimit((n) => n + PAGE_SIZE)}
            >
              Load {Math.min(PAGE_SIZE, filteredCount - visibleLimit)} more
            </button>
          </div>
        )}
      </>
    );
  };

  return (
    <section className={styles.section}>
      <div className={styles.header}>
        <h2 className={styles.heading}>
          Asset Assignment ·{' '}
          {totalCount === 1 ? '1 fridge' : `${totalCount} fridges`}
        </h2>
        <p className={styles.helper}>Fridges currently playing this video.</p>
      </div>

      {totalCount > 0 && (
        <div className={styles.toolbar}>
          <div className={styles.searchGroup}>
            <input
              type="search"
              className={styles.searchInput}
              placeholder="search by fridge id…"
              aria-label="Search fridges by ID"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <span className={styles.resultCount}>
              {hasSearch
                ? `${filteredCount} of ${totalCount}`
                : `${totalCount} ${totalCount === 1 ? 'fridge' : 'fridges'}`}
            </span>
          </div>
          <div className={styles.actionRow}>
            <button
              type="button"
              className={styles.actionButton}
              onClick={handleCopyFridgeList}
              disabled={filteredCount === 0}
            >
              Copy fridge list
            </button>
            <button
              type="button"
              className={styles.actionButton}
              onClick={handleDownloadCsv}
              disabled={filteredCount === 0}
            >
              Download CSV
            </button>
          </div>
        </div>
      )}

      {renderBody()}
    </section>
  );
};

AssetAssignmentSection.propTypes = {
  sleepScreenId: PropTypes.string.isRequired,
  forceRefresh: PropTypes.bool,
};

AssetAssignmentSection.defaultProps = {
  forceRefresh: false,
};

export default AssetAssignmentSection;
